import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AnalyzeForm from "../forms/AnalyzeForm";
import { SITE_URL } from "../../content/homepageSeo";
import { notifyPendingTestResultsChanged } from "../../utils/pendingTestResults";
import PerformanceMetrics from "../metrics/performance/PerformanceMetrics";
import TaglineWithSVG from "../common/TaglineWithSVG";
import { PacmanLoader } from "react-spinners";

function TestPage({ isOffline, setTaskId, aiModel, setAiModel }) {
  const [searchParams] = useSearchParams();
  const [metrics, setMetrics] = useState({
    desktop: null,
    mobile: null,
  });
  const [loading, setLoading] = useState({
    desktop: false,
    mobile: false,
  });
  const [errors, setErrors] = useState({
    desktop: null,
    mobile: null,
  });
  const [progress, setProgress] = useState({
    desktop: 0,
    mobile: 0,
  });
  const [activeStrategy, setActiveStrategy] = useState("desktop");
  const [hasRunTest, setHasRunTest] = useState(false);
  // Replace single pollingInterval with an object for both strategies
  const [pollingIntervals, setPollingIntervals] = useState({
    desktop: null,
    mobile: null,
  });
  // Use metrics to pre-fill the URL field
  const [url, setUrl] = useState(() => searchParams.get("url") || "");

  useEffect(() => {
    const urlParam = searchParams.get("url");
    if (urlParam) setUrl(urlParam);
  }, [searchParams]);

  // When starting a new test, clear both polling intervals
  const handleRunTest = () => {
    setHasRunTest(true);
    setErrors({ desktop: null, mobile: null });
    // Clear both polling intervals
    Object.values(pollingIntervals).forEach((interval) => {
      if (interval) clearInterval(interval);
    });
    setPollingIntervals({ desktop: null, mobile: null });
  };

  // Polling function for progress updates (per strategy)
  const startPolling = (taskId, strategy) => {
    // Clear any existing interval for this strategy
    if (pollingIntervals[strategy]) {
      clearInterval(pollingIntervals[strategy]);
    }

    const interval = setInterval(async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND_URL}/api/metrics/progress/${taskId}`,
          {
            credentials: "include",
          },
        );
        if (response.ok) {
          const data = await response.json();
          if (data.progress !== undefined) {
            setProgress((prev) => ({
              ...prev,
              [strategy]: data.progress,
            }));
          }
          // If progress is complete, stop polling for this strategy
          if (data.progress >= 100) {
            clearInterval(interval);
            setPollingIntervals((prev) => ({ ...prev, [strategy]: null }));
          }
        }
      } catch (error) {
        // Silent error handling for polling
        console.log("Polling error (normal):", error.message);
      }
    }, 2000);

    setPollingIntervals((prev) => ({ ...prev, [strategy]: interval }));
  };

  // Cleanup polling on unmount or when starting a new test
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals).forEach((interval) => {
        if (interval) clearInterval(interval);
      });
    };
  }, [pollingIntervals]);

  // Utility to get a localStorage item (no expiration)
  function getLocalStorageItem(key) {
    const saved = localStorage.getItem(key);
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }

  // Restore metrics from localStorage on mount, and handle auto-clear after 10 minutes
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => {
    const pending = getLocalStorageItem("pendingTestResults");
    const now = Date.now();
    const TEN_MINUTES = 10 * 60 * 1000;
    if (pending) {
      const savedAtValue = pending.savedAt || 0;
      setSavedAt(savedAtValue);
      if (now - savedAtValue >= TEN_MINUTES) {
        // Expired: clear
        handleClearAll();
      } else {
        setMetrics(pending.metrics || pending);
        // Pre-fill URL from metrics if available
        const lastUrl =
          pending.metrics?.desktop?.url || pending.metrics?.mobile?.url || "";
        if (lastUrl) setUrl(lastUrl);
        // Set timeout to clear when 10 minutes are up
        const timeout = setTimeout(
          () => {
            handleClearAll();
            // Optionally, refresh the page
            window.location.reload();
          },
          TEN_MINUTES - (now - savedAtValue),
        );
        return () => clearTimeout(timeout);
      }
    }
  }, []);

  // Persist metrics to localStorage whenever they change, with timestamp
  useEffect(() => {
    if (metrics && (metrics.desktop || metrics.mobile)) {
      const now = Date.now();
      setSavedAt(now);
      localStorage.setItem(
        "pendingTestResults",
        JSON.stringify({ metrics, savedAt: now }),
      );
      notifyPendingTestResultsChanged();
    }
  }, [metrics]);

  // Handler to clear results and state, and localStorage
  const handleClearAll = () => {
    localStorage.removeItem("pendingTestResults");
    notifyPendingTestResultsChanged();
    setUrl("");
    setMetrics({ desktop: null, mobile: null });
    setHasRunTest(false);
  };

  // Show tagline only if no test has been run and no metrics exist
  const hasResults = !!metrics.desktop || !!metrics.mobile;
  const showTagline = !hasRunTest && !hasResults;
  // Show loading spinner only if not showing tagline AND current strategy is loading
  const showLoading =
    !showTagline && loading[activeStrategy] && !errors[activeStrategy];
  // Show metrics if they exist for current strategy and not loading/error
  const showMetrics =
    !showTagline &&
    metrics[activeStrategy] &&
    !loading[activeStrategy] &&
    !errors[activeStrategy];
  // Show error if exists for current strategy
  const showError =
    !showTagline && errors[activeStrategy] && !loading[activeStrategy];

  useEffect(() => {
    const handleStorage = (e) => {
      // Only clear if pendingTestResults or lastTestUrl are removed
      if (e.key === "pendingTestResults" && e.newValue === null) {
        setUrl("");
        setMetrics({ desktop: null, mobile: null });
        setHasRunTest(false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <>
      <Helmet>
        <title>Run a Free Website Speed Test | PageTest</title>
        <meta
          name="description"
          content="Run a free Google Lighthouse speed test on any URL. Desktop and mobile Core Web Vitals, issue breakdowns, shareable reports, and optional AI fix suggestions."
        />
        <link rel="canonical" href={`${SITE_URL}/test`} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <motion.div
        className="mx-auto p-6 mt-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
      <AnalyzeForm
        setMetrics={setMetrics}
        loading={loading}
        setLoading={setLoading}
        progress={progress}
        setProgress={setProgress}
        activeStrategy={activeStrategy}
        setActiveStrategy={setActiveStrategy}
        isOffline={isOffline}
        setTaskId={setTaskId}
        onRunTest={handleRunTest}
        setErrors={setErrors}
        errors={errors}
        startPolling={startPolling}
        url={url}
        setUrl={setUrl}
        onClearAll={handleClearAll}
        hasRunTest={hasRunTest || hasResults}
        aiModel={aiModel}
      />

      <AnimatePresence mode="wait">
        {showTagline && (
          <motion.div
            key="tagline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <TaglineWithSVG />
          </motion.div>
        )}

        {showLoading && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-center items-center my-12"
          >
            <div className="flex flex-col items-center">
              <PacmanLoader color="#14b8a6" size={25} />
              <p className="mt-4 font-semibold text-muted-foreground">
                Loading {activeStrategy} insights... {progress[activeStrategy]}%
              </p>
            </div>
          </motion.div>
        )}

        {showError && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-center items-center my-12"
          >
            <div className="flex flex-col items-center bg-red-50 p-6 rounded-lg max-w-2xl w-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-10 w-10 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-red-800">
                {activeStrategy === "desktop" ? "Desktop" : "Mobile"} Test
                Failed
              </h3>
              <p className="mt-1 text-sm text-red-600 text-center">
                {errors[activeStrategy]}
              </p>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                Please try running the test again. If the problem persists,
                check your URL or try later.
              </p>
            </div>
          </motion.div>
        )}

        {showMetrics && (
          <motion.div
            key="metrics"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <PerformanceMetrics
              metrics={metrics[activeStrategy]}
              allMetrics={metrics}
              knownIssues={metrics[activeStrategy]?.knownIssues}
              onClearResults={handleClearAll}
              savedAt={savedAt}
              aiModel={aiModel}
              setAiModel={setAiModel}
            />
          </motion.div>
        )}
      </AnimatePresence>
      </motion.div>
    </>
  );
}

export default TestPage;
